import { createContext, useContext } from 'react';
import type { AppConfig } from '@/types';

/**
 * React Context for runtime configuration loaded from `public/config.json`.
 * Provided once at the app root so any component can access config values
 * without prop drilling or module-level singletons.
 */
export const AppConfigContext = createContext<AppConfig | null>(null);

let configPromise: Promise<AppConfig> | null = null;

export function useAppConfig(): AppConfig {
  const config = useContext(AppConfigContext);
  if (!config) {
    throw new Error('useAppConfig must be used within <AppConfigContext.Provider>');
  }
  return config;
}

/**
 * Fetches `public/config.json` once. Called in `main.tsx` before rendering
 * and the result is passed into AppConfigContext.Provider.
 */
export async function loadConfig(): Promise<AppConfig> {
  if (!configPromise) {
    configPromise = (async () => {
      const response = await fetch('/config.json');
      if (!response.ok) {
        throw new Error(`Failed to load config.json: ${response.status}`);
      }

      const config = (await response.json()) as AppConfig;
      if (!config.api?.baseUrl || !config.api.baseUrl.trim()) {
        throw new Error('api.baseUrl is not set in config.json.');
      }

      return config;
    })();
  }

  return configPromise;
}

/**
 * Returns API base URL from cached runtime config.
 * Use this helper in services to avoid repeating config loading logic.
 */
export async function getApiBaseUrl(): Promise<string> {
  const config = await loadConfig();
  return config.api.baseUrl;
}
